import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Radio, Eye, EyeOff } from "lucide-react";

export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [check, setCheck] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!token) { setCheck({ ok: false, error: "Missing or invalid link." }); return; }
    api.get(`/auth/set-password/${encodeURIComponent(token)}`)
      .then((r) => setCheck(r.data))
      .catch((e) => setCheck({ ok: false, error: e.response?.data?.detail || "This link is invalid." }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { setError("Passwords do not match"); return; }
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/set-password", { token, new_password: pw });
      if (data.signed_in) { localStorage.setItem("vis_token", data.token); window.location.href = "/"; return; }
      setDone(data.message || "Password saved.");
    } catch (err) { setError(err.response?.data?.detail || "Could not set password"); } finally { setBusy(false); }
  };

  const field = (label, val, set, tid, ph) => (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">{label}</label>
      <div className="relative mt-1">
        <input data-testid={tid} type={showPw ? "text" : "password"} value={val} onChange={(e) => set(e.target.value)} placeholder={ph}
          className="w-full border border-[#DFDAD0] px-3 py-2 pr-10 text-sm outline-none focus:border-[#1C2530]" />
        <button type="button" data-testid={`${tid}-toggle`} onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-[#1C2530]/50 hover:text-[#1C2530]">
          {showPw ? <EyeOff className="w-4 h-4" strokeWidth={1.75} /> : <Eye className="w-4 h-4" strokeWidth={1.75} />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F3EE] p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 bg-[#1C2530] flex items-center justify-center"><Radio className="w-5 h-5 text-[#F5F3EE]" strokeWidth={1.75} /></div>
          <div className="font-mono text-sm font-semibold text-[#1C2530]">SET YOUR PASSWORD</div>
        </div>
        <div className="bg-white border border-[#DFDAD0] p-8 shadow-[3px_3px_0px_#DFDAD0]" data-testid="set-password-card">
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#1C2530]/60">One-time link · expires in 24h</div>
          <h1 className="text-2xl font-semibold tracking-tight text-[#1C2530] mt-1 mb-4">{check?.name ? `Welcome, ${check.name}` : "Choose a password"}</h1>
          {!check ? (
            <div className="text-sm text-[#1C2530]/60">Checking your link…</div>
          ) : !check.ok ? (
            <div className="text-sm text-[#B23A2E]" data-testid="set-password-invalid">{check.error}</div>
          ) : done ? (
            <div className="text-sm text-[#2F7A3E]" data-testid="set-password-done">{done}</div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="text-xs text-[#1C2530]/60">Account: <span className="font-mono" data-testid="set-password-email">{check.email_hint}</span></div>
              {field("New password", pw, setPw, "set-password-new", "At least 6 characters")}
              {field("Confirm password", pw2, setPw2, "set-password-confirm", "Repeat it")}
              {error && <div data-testid="set-password-error" className="font-mono text-xs text-[#B23A2E] border border-[#B23A2E]/30 bg-[#B23A2E]/5 px-3 py-2">{error}</div>}
              <button type="submit" disabled={busy} data-testid="set-password-submit"
                className="w-full bg-[#1C2530] text-[#F5F3EE] py-2.5 text-sm font-medium hover:bg-[#2a3644] disabled:opacity-50">
                {busy ? "Saving…" : "Set password & sign in"}
              </button>
            </form>
          )}
        </div>
        <Link to="/login" className="mt-4 inline-block font-mono text-[10px] uppercase text-[#1C2530]/60 hover:text-[#1C2530]">Back to sign in</Link>
      </div>
    </div>
  );
}
